import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { User, Ticket, LogOut, Settings, Video, Menu, X } from "lucide-react";
import NotificationBell from "./NotificationBell";
import BrandLogo from "./BrandLogo";

const Navbar = ({ user, onLogout }) => {
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const closeMobile = () => setMobileOpen(false);
  const accountMenuItemClass = "min-h-12 gap-3 rounded-lg px-4 py-3 text-[15px] leading-5 text-gray-200 outline-none transition-colors focus:bg-white/10 focus:text-white";

  const handleLogin = () => {
    // Single unified sign-in page (Google + Facebook + Instagram + X)
    navigate("/");
  };

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 bg-gradient-to-b from-black/90 to-transparent backdrop-blur-sm">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          <div className="flex items-center gap-8">
            <Link to="/events" className="flex items-center" data-testid="logo-link">
              <BrandLogo />
            </Link>

            {/* Every signed-in user is both a viewer and a creator, so we show
                the full nav: viewer links + a Creator Studio entry point. */}
            <div className="hidden md:flex items-center gap-6">
              <Link to="/events" className="text-gray-300 hover:text-white text-sm font-medium" data-testid="home-link">
                Home
              </Link>
              <Link to="/browse" className="text-gray-300 hover:text-white text-sm font-medium" data-testid="browse-link">
                Browse
              </Link>
              <Link to="/my-tickets" className="text-gray-300 hover:text-white text-sm font-medium" data-testid="my-tickets-link">
                My Tickets
              </Link>
              <Link
                to="/creator/dashboard"
                className="flex items-center gap-1.5 text-white text-sm font-semibold px-4 py-1.5 rounded-full bg-blue-600/20 border border-blue-500/40 hover:bg-blue-600/30 transition-colors"
                data-testid="creator-studio-link"
              >
                <Video className="w-3.5 h-3.5 text-blue-400" />
                Creator Studio
              </Link>
              <Link to="/about" className="text-gray-300 hover:text-white text-sm font-medium" data-testid="about-link">
                About
              </Link>
            </div>
          </div>

          <div className="flex items-center gap-3">
            {/* Mobile menu toggle — shown only below md */}
            <button
              className="md:hidden flex items-center justify-center h-9 w-9 text-gray-200 hover:text-white"
              onClick={() => setMobileOpen((o) => !o)}
              aria-label="Toggle navigation menu"
              aria-expanded={mobileOpen}
              data-testid="mobile-menu-button"
            >
              {mobileOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
            {user ? (
              <>
                <NotificationBell user={user} />
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button className="flex items-center gap-2 focus:outline-none" data-testid="user-menu-button">
                      <Avatar className="h-8 w-8">
                        <AvatarImage src={user.picture} alt={user.name} />
                        <AvatarFallback className="bg-blue-600 text-white font-semibold">{user.name?.charAt(0)?.toUpperCase()}</AvatarFallback>
                      </Avatar>
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent
                    align="end"
                    sideOffset={8}
                    className="w-[min(20rem,calc(100vw-1rem))] rounded-2xl border-gray-700/80 bg-black/95 p-2 shadow-2xl shadow-black/60 backdrop-blur-xl"
                  >
                    <DropdownMenuItem className={accountMenuItemClass} data-testid="profile-menu-item">
                      <User className="h-5 w-5 text-gray-400" />
                      <span className="min-w-0 truncate font-medium">{user.username ? `@${user.username}` : user.name}</span>
                    </DropdownMenuItem>
                    <DropdownMenuSeparator className="my-2 bg-gray-800" />
                    <DropdownMenuItem
                      onClick={() => navigate("/creator/dashboard")}
                      className={`${accountMenuItemClass} cursor-pointer`}
                      data-testid="dashboard-menu-item"
                    >
                      <Video className="h-5 w-5 text-blue-400" />
                      <span className="whitespace-nowrap font-medium">Creator Studio</span>
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={() => navigate("/creator/settings")}
                      className={`${accountMenuItemClass} cursor-pointer`}
                      data-testid="settings-menu-item"
                    >
                      <Settings className="h-5 w-5 text-gray-400" />
                      <span className="whitespace-nowrap font-medium">Profile &amp; Payout Settings</span>
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={() => navigate("/my-tickets")}
                      className={`${accountMenuItemClass} cursor-pointer`}
                      data-testid="tickets-menu-item"
                    >
                      <Ticket className="h-5 w-5 text-gray-400" />
                      <span className="whitespace-nowrap font-medium">My Tickets</span>
                    </DropdownMenuItem>
                    <DropdownMenuSeparator className="my-2 bg-gray-800" />
                    <DropdownMenuItem
                      onClick={onLogout}
                      className={`${accountMenuItemClass} cursor-pointer text-red-200 focus:bg-red-500/15 focus:text-red-100`}
                      data-testid="logout-button"
                    >
                      <LogOut className="h-5 w-5 text-red-300" />
                      <span className="whitespace-nowrap font-medium">Logout</span>
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </>
            ) : (
              <Button
                onClick={handleLogin}
                className="bg-blue-600 hover:bg-blue-700 text-white font-medium px-6"
                data-testid="login-button"
              >
                Sign In
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Mobile dropdown menu */}
      {mobileOpen && (
        <div className="md:hidden bg-black/95 backdrop-blur-sm border-t border-gray-800" data-testid="mobile-menu">
          <div className="px-4 py-3 flex flex-col gap-1">
            <Link to="/events" onClick={closeMobile} className="text-gray-200 hover:text-white text-base font-medium py-2.5" data-testid="mobile-home-link">
              Home
            </Link>
            <Link to="/browse" onClick={closeMobile} className="text-gray-200 hover:text-white text-base font-medium py-2.5" data-testid="mobile-browse-link">
              Browse
            </Link>
            <Link to="/my-tickets" onClick={closeMobile} className="text-gray-200 hover:text-white text-base font-medium py-2.5" data-testid="mobile-my-tickets-link">
              My Tickets
            </Link>
            <Link
              to="/creator/dashboard"
              onClick={closeMobile}
              className="flex items-center gap-2 text-white text-base font-semibold py-2.5"
              data-testid="mobile-creator-studio-link"
            >
              <Video className="w-4 h-4 text-blue-400" />
              Creator Studio
            </Link>
            <Link to="/about" onClick={closeMobile} className="text-gray-200 hover:text-white text-base font-medium py-2.5" data-testid="mobile-about-link">
              About
            </Link>
            {!user && (
              <Button
                onClick={() => { closeMobile(); handleLogin(); }}
                className="mt-2 bg-blue-600 hover:bg-blue-700 text-white font-medium w-full"
                data-testid="mobile-login-button"
              >
                Sign In
              </Button>
            )}
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navbar;
